import { createUserWithEmailAndPassword } from "firebase/auth";
import {
    collection,
    deleteDoc,
    doc,
    getDocs,
    setDoc,
    updateDoc
} from "firebase/firestore";
import { useEffect, useState } from "react";

import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography
} from "@mui/material";

import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { owneruid } from "../constant/services";
import { auth, db } from "../firebase-key";

const WebAdmin = () => {
    const navigate = useNavigate();

    const [tab, setTab] = useState("teams");
    const [teams, setTeams] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [fetching, setFetching] = useState(false);

    // Create/Edit Team Popup
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [teamForm, setTeamForm] = useState({
        uid: "",
        name: "",
        email: "",
        address: "",
        pan: "",
        aadhaar: "",
        age: "",
        gender: "",
        password: ""
    });

    // Load Data
    const fetchData = async () => {
        try {
            setFetching(true);

            const teamSnap = await getDocs(collection(db, "teams"));
            const customerSnap = await getDocs(collection(db, "users"));

            setTeams(teamSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setCustomers(customerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) {
            toast.error("Failed to load data");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ------------------------------
    // Handle team form change
    // ------------------------------
    const handleChange = (field, value) => {
        setTeamForm({ ...teamForm, [field]: value });
    };

    // ------------------------------
    // Create new team
    // ------------------------------
    const createTeam = async () => {
        const { name, email, password, address, aadhaar, pan, age, gender } =
            teamForm;

        if (!email || !password) {
            toast.error("Email & Password required");
            return;
        }

        try {
            // Create Auth User
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            const uid = cred.user.uid;

            // Store team in Firestore
            await setDoc(doc(db, "teams", uid), {
                uid,
                name,
                email,
                address,
                pan,
                aadhaar,
                customers:[],
                age,
                gender,
                created_at: new Date().toISOString()
            });

            toast.success("Team created successfully!");
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    // ------------------------------
    // Edit existing team
    // ------------------------------
    const updateTeam = async () => {
        try {
            const { uid, ...rest } = teamForm;
            await updateDoc(doc(db, "teams", uid), rest);

            toast.success("Team updated!");
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            toast.error("Failed to update team");
        }
    };

    // ------------------------------
    // Delete team
    // ------------------------------
    const deleteTeam = async (uid) => {
        try {
            await deleteDoc(doc(db, "teams", uid));
            toast.success("Team deleted");
            fetchData();
        } catch (err) {
            toast.error("Failed to delete team");
        }
    };

    // ------------------------------
    // Open create team popup
    // ------------------------------
    const openCreatePopup = () => {
        setIsEditing(false);
        setTeamForm({
            uid: "",
            name: "",
            email: "",
            address: "",
            pan: "",
            aadhaar: "",
            age: "",
            gender: "",
            password: ""
        });
        setDialogOpen(true);
    };

    // ------------------------------
    // Open edit team popup
    // ------------------------------
    const openEditPopup = (team) => {
        setIsEditing(true);
        setTeamForm({
            uid: team.id,
            name: team.name || "",
            email: team.email || "",
            address: team.address || "",
            pan: team.pan || "",
            aadhaar: team.aadhaar || "",
            age: team.age || "",
            gender: team.gender || "",
            password: "" // cannot fetch old password
        });
        setDialogOpen(true);
    };

    if (fetching) {
        return <div>Loading...</div>
    }

    return (
        <Box sx={{ px: 6, py: 16, minHeight: "100vh" }}>
            <ToastContainer />

            {/* TABS */}
            <Box display="flex" justifyContent="center" mb={6} gap={2}>
                <Button
                    variant={tab === "teams" ? "contained" : "outlined"}
                    onClick={() => setTab("teams")}
                >
                    Team Management
                </Button>

                <Button
                    variant={tab === "customers" ? "contained" : "outlined"}
                    onClick={() => setTab("customers")}
                >
                    Customer Management
                </Button>
            </Box>

            {/* TEAM MANAGEMENT */}
            {tab === "teams" && (
                <Box>
                    <Button variant="contained" onClick={openCreatePopup}>
                        + Add Team Member
                    </Button>

                    <Grid container spacing={3} sx={{ mt: 3 }}>
                        {teams.map((t) => (
                            <Grid item xs={12} md={6} key={t.id}>
                                <Box
                                    sx={{
                                        p: 3,
                                        background: "white",
                                        borderRadius: 2,
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center"
                                    }}
                                >
                                    <Box>
                                        <Typography fontWeight={600}>{t.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t.email}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <IconButton onClick={() => openEditPopup(t)}>
                                            <FaEdit color="#0ea5e9" />
                                        </IconButton>

                                        {t.uid !== owneruid && <IconButton onClick={() => deleteTeam(t.id)}>
                                            <FaTrash color="#dc2626" />
                                        </IconButton>}

                                        <IconButton onClick={() => navigate(`/team/${t.uid}`)}>
                                            <FaEye color="#16a34a" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* CUSTOMER MANAGEMENT */}
            {tab === "customers" && (
                <Grid container spacing={3}>
                    {customers.map((c) => (
                        <Grid item xs={12} md={6} key={c.id}>
                            <Box
                                sx={{
                                    p: 3,
                                    background: "white",
                                    borderRadius: 2,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                }}
                            >
                                <Box>
                                    <Typography fontWeight={600}>
                                        {c.customer_name || c.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {c.email}
                                    </Typography>
                                </Box>

                                <IconButton onClick={() => navigate(`/user/${c.uid}`)}>
                                    <FaEye color="#16a34a" />
                                </IconButton>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* CREATE / EDIT TEAM POPUP */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {isEditing ? "Edit Team Member" : "Create Team Member"}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        {(isEditing ? [
                            { label: "Name", key: "name" },
                            { label: "Email", key: "email", disabled: isEditing },
                            { label: "Address", key: "address" },
                            { label: "PAN", key: "pan" },
                            { label: "Aadhaar", key: "aadhaar" },
                            { label: "Age", key: "age" },
                            { label: "Gender", key: "gender" }
                        ] : [
                            { label: "Name", key: "name" },
                            { label: "Email", key: "email", disabled: isEditing },
                            { label: "Address", key: "address" },
                            { label: "PAN", key: "pan" },
                            { label: "Password", key: "password" },
                            { label: "Aadhaar", key: "aadhaar" },
                            { label: "Age", key: "age" },
                            { label: "Gender", key: "gender" }
                        ]).map((f) => (
                            <Grid item xs={12} sm={6} key={f.key}>
                                {f.key === "gender" ? (
                                    <TextField
                                        select
                                        fullWidth
                                        label="Gender"
                                        value={teamForm.gender}
                                        onChange={(e) => handleChange("gender", e.target.value)}
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </TextField>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label={f.label}
                                        value={teamForm[f.key]}
                                        disabled={f.disabled}
                                        onChange={(e) => handleChange(f.key, e.target.value)}
                                    />
                                )}
                            </Grid>
                        ))}
                    </Grid>

                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3 }}
                        onClick={isEditing ? updateTeam : createTeam}
                    >
                        {isEditing ? "Save Changes" : "Create Team"}
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default WebAdmin;
